import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, cachedUser, speciesApi, surveyApi } from '../services/api';
import { mirrorSession } from '../services/http';
import { requestPersistentStorage } from '../services/offlineStore';
import { startAutoSync, syncNow } from '../services/syncEngine';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { surveyAllows } from '../utils/surveyAccess';

const AuthContext = createContext(null);

// The API returns roles and the effective permissions derived from them.
const normalizeUser = (userData) => ({
  ...userData,
  roles: userData.roles || [],
  permissions: userData.permissions || [],
});

const errorMessage = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.msg || fallback;

// Refresh the device's copy of the data needed offline: the whole survey tree
// (every survey, transect and quadrat page can be built from it) and the
// species list. Runs in the background; failures just mean an older copy.
const saveForOffline = () => {
  surveyApi.getAll().catch(() => {});
  speciesApi.getAll().catch(() => {});
};

// Sync triggers must exist whenever someone is signed in, including when the
// app starts with no signal: that's when changes are waiting and the 'online'
// event will be the cue to send them.
const startSyncing = () => {
  startAutoSync();
  syncNow();
};

// Offline copies and syncing wait while the account still has a password an
// admin chose: the server refuses everything else until the user picks their own.
const startSession = (user) => {
  if (user.must_change_password) return;
  saveForOffline();
  startSyncing();
};

// Remember who is signed in so the session survives starting with no signal
// (and so the service worker can sync for them while the app is closed).
const rememberUser = (userData) => {
  const user = normalizeUser(userData);
  cachedUser.set(user);
  mirrorSession();
  return user;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Restore the session on first load. If the stored access token has expired
  // the API client refreshes it transparently; if that fails it clears the
  // session and fires 'auth:logout', handled below.
  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      if (!localStorage.getItem('access_token')) {
        setLoading(false);
        return;
      }
      // Start straight away as the remembered user (instant, and works with no
      // signal), then confirm with the server in the background.
      const remembered = cachedUser.get();
      if (remembered) {
        setUser(remembered);
        setLoading(false);
        mirrorSession();
        if (!remembered.must_change_password) startSyncing();
      }
      try {
        const userData = await authApi.me();
        if (!cancelled) {
          const current = rememberUser(userData);
          setUser(current);
          startSession(current);
        }
      } catch (error) {
        const status = error.response?.status;
        if (!cancelled && (status === 401 || status === 403)) {
          authApi.logout();
        }
        // A network error leaves the remembered user signed in.
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fired by the API client whenever the session is cleared (explicit logout
  // or a failed token refresh).
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      navigate('/login');
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [navigate]);

  // Fired by the API client when the server says this account must choose a
  // new password (an admin reset it mid-session).
  useEffect(() => {
    const handlePasswordChangeRequired = () => {
      setUser((current) =>
        current && !current.must_change_password
          ? rememberUser({ ...current, must_change_password: true })
          : current
      );
    };
    window.addEventListener('auth:password-change-required', handlePasswordChangeRequired);
    return () =>
      window.removeEventListener('auth:password-change-required', handlePasswordChangeRequired);
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { access_token, refresh_token, user: userData } = await authApi.login({
        email,
        password,
      });
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      const current = rememberUser(userData);
      setUser(current);
      requestPersistentStorage();
      startSession(current);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: errorMessage(error, 'Unable to log in. Please try again.'),
      };
    }
  }, []);

  const logout = useCallback(() => {
    authApi.logout();
  }, []);

  // Throws on failure so the form can show the error. Changing the password
  // clears must_change_password, which is what lets a new user into the app.
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const { user: userData } = await authApi.changePassword(currentPassword, newPassword);
    const current = rememberUser(userData);
    setUser(current);
    startSession(current);
  }, []);

  const hasRole = useCallback((role) => Boolean(user?.roles.includes(role)), [user]);

  const hasPermission = useCallback(
    (permission) => {
      if (!user) return false;
      if (user.roles.includes('admin')) return true;
      return user.permissions.includes(permission);
    },
    [user]
  );

  // The role's permission, and (given a survey) the user's access to that survey.
  const can = useCallback(
    (permission, survey) => hasPermission(permission) && surveyAllows(survey, permission),
    [hasPermission]
  );

  const value = useMemo(
    () => ({ user, loading, login, logout, changePassword, hasPermission, hasRole, can }),
    [user, loading, login, logout, changePassword, hasPermission, hasRole, can]
  );

  return (
    <AuthContext.Provider value={value}>
      {loading ? <LoadingSpinner fullScreen /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
